import { ChangeEvent, memo, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { CloseIcon, DeleteIcon, FileIcon, SearchIcon } from '~/assets/icons';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { BaseImage, BaseSelect, BaseUploadFile } from '~/components/common';
import { OptionUploadFile, UploadFile, UploadFileWithNote } from '~/utils/types/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { ITag } from '~/utils/interface/tag';
import { IFolder } from '~/utils/interface/folder';
import { catchError, handleApiError, notify } from '~/utils/helper';
import { handleCreateJobUploadFiles, handleGetJobFiles, handleGetJobFolders } from '~/thunks/job/jobThunk';
import { CREATE_CUSTOMER_FILE, CREATE_ESTIMATE_FILE, CREATE_JOB_FILE } from '~/utils/constants/jobs';
import { FileTypeEnum } from '~/utils/constants/file';
import { IJob, IJobFile } from '~/utils/interface/job';
import { handleCreateCustomerUploadFiles, handleGetFiles } from '~/thunks/customer/customerThunk';
import { handleCreateEstimateUploadFiles } from '~/thunks/estimate-template/estimateTemplateThunk';
import useQueue from '~/hooks/useQueue';
import { adminRouteAbsolute } from '~/utils/constants/route';
import { ICustomer } from '~/utils/interface/customer';
import { CUSTOMER_TYPES_ORGANIZATION } from '~/utils/constants/customers';

type UploadFileProps = {
  jobId?: string;
  customerId?: string;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  open: boolean;
  optionUploadFile: OptionUploadFile;
  type?: string;
  editData?: any[] | undefined;
  selectedFiles?: (file: Array<IJobFile>) => void;
  uploadedToFolder?: (folderId: string) => void;
  data?: {};
  shouldQueue?: boolean;
  job?: IJob;
  customer?: ICustomer;
};

type UploadFiles = {
  folder?: string;
  tags?: ITag[];
  files?: Array<UploadFileWithNote>;
};
const initialValues: UploadFiles = {};
interface TabPanelProps {
  children?: React.ReactNode;
  index: string;
  value: string;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function UploadFileDialog(props: UploadFileProps) {
  const { t } = useTranslation(['admin/job']);
  const dispatch = useAppDispatch();
  const [value, setValue] = useState('uploadFile');
  const [uploadFiles, setUploadFiles] = useState<Array<UploadFileWithNote>>([]);
  const [selectedFiles, setSelectedFiles] = useState<Array<IJobFile>>([]);
  const [submitForm, setSubmitForm] = useState<boolean>(false);

  const [jobFile, setJobFile] = useState<Array<IJobFile>>([]);

  const [folders, setFolders] = useState<Array<IFolder>>([]);
  const [disableSubmit, setDisableSubmit] = useState(false);

  const { tags } = useAppSelector((state) => state.tag);
  const queue = useQueue();

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      setDisableSubmit(true);
      const formData = new FormData();
      uploadFiles &&
        uploadFiles.forEach((file, index) => {
          file.file && formData.append(`files[${index}][file]`, file.file);
          formData.append(`files[${index}][name]`, file.name + '.' + file.fileExtension);
          formData.append(`files[${index}][type]`, file.type ?? FileTypeEnum.ANY);
          formData.append(`files[${index}][folderId]`, values.folder ?? '');
          formData.append(`files[${index}][note]`, file.note ?? '');
        });
      values.tags &&
        // eslint-disable-next-line array-callback-return
        values.tags.map((tag, index) => {
          formData.append(`tagIds[${index}]`, tag.id);
        });

      // Upload in background
      if (props.shouldQueue) {
        if (props.type === CREATE_JOB_FILE && Object.keys(uploadFiles).length) {
          queue.process({
            handler: async () => {
              const newJobFiles = await dispatch(
                handleCreateJobUploadFiles({ id: props.jobId, data: formData })
              ).unwrap();
              newJobFiles && setSelectedFiles([...selectedFiles, ...newJobFiles]);
              props.uploadedToFolder && values.folder && props.uploadedToFolder(values.folder);
              setSubmitForm(true);
            },
            processNotify: t('files.queue.uploadFile.processNotify', { jobName: props?.job?.name }),
            processTitle: t('files.queue.uploadFile.processTitle', { jobName: props?.job?.name }),
            successTitle: t('files.queue.uploadFile.successTitle', { jobName: props?.job?.name }),
            errorTitle: t('files.queue.uploadFile.errorTitle', { jobName: props?.job?.name }),
            navigateUrl: adminRouteAbsolute.jobDetail.replace(':id', `${String(props.jobId)}#files`),
          });
        }

        if (props.type === CREATE_CUSTOMER_FILE && Object.keys(uploadFiles).length) {
          let navigateUrl = '';
          if (props.customer?.type === CUSTOMER_TYPES_ORGANIZATION) {
            navigateUrl = adminRouteAbsolute.customerDetail.replace(':id', `${String(props.customerId)}#files`);
          } else {
            navigateUrl = adminRouteAbsolute.contactDetail.replace(':id', `${String(props.customerId)}#files`);
          }

          queue.process({
            handler: async () => {
              const newCustomerFiles = await dispatch(
                handleCreateCustomerUploadFiles({ id: props.customerId, data: formData })
              ).unwrap();
              newCustomerFiles && setSelectedFiles([...selectedFiles, ...newCustomerFiles]);
              props.uploadedToFolder && values.folder && props.uploadedToFolder(values.folder);
              setSubmitForm(true);
            },
            processNotify: t('files.queue.uploadFile.processNotify', {
              customerName: props?.customer?.name,
              customerType: props?.customer?.type,
              ns: 'admin/customer',
            }),
            processTitle: t('files.queue.uploadFile.processTitle', {
              customerName: props?.customer?.name,
              customerType: props?.customer?.type,
              ns: 'admin/customer',
            }),
            successTitle: t('files.queue.uploadFile.successTitle', {
              customerName: props?.customer?.name,
              customerType: props?.customer?.type,
              ns: 'admin/customer',
            }),
            errorTitle: t('files.queue.uploadFile.errorTitle', {
              customerName: props?.customer?.name,
              customerType: props?.customer?.type,
              ns: 'admin/customer',
            }),
            navigateUrl,
          });
        }

        handleSetOpen(false);
        setDisableSubmit(false);

        return;
      }

      try {
        if (props.type === CREATE_JOB_FILE && Object.keys(uploadFiles).length) {
          const newJobFiles = await dispatch(handleCreateJobUploadFiles({ id: props.jobId, data: formData })).unwrap();
          newJobFiles && setSelectedFiles([...selectedFiles, ...newJobFiles]);
          props.uploadedToFolder && values.folder && props.uploadedToFolder(values.folder);
          notify('success', t('createSuccess', { ns: 'admin/common' }));
        } else if (props.type === CREATE_CUSTOMER_FILE && Object.keys(uploadFiles).length) {
          const newCustomerFiles = await dispatch(
            handleCreateCustomerUploadFiles({ id: props.customerId, data: formData })
          ).unwrap();
          newCustomerFiles && setSelectedFiles([...selectedFiles, ...newCustomerFiles]);
          props.uploadedToFolder && values.folder && props.uploadedToFolder(values.folder);
          notify('success', t('createSuccess', { ns: 'admin/common' }));
        } else if (props.type === CREATE_ESTIMATE_FILE && Object.keys(uploadFiles).length) {
          const newEstimateFiles = await dispatch(handleCreateEstimateUploadFiles({ data: formData })).unwrap();
          newEstimateFiles && setSelectedFiles([...selectedFiles, ...newEstimateFiles]);
          props.uploadedToFolder && values.folder && props.uploadedToFolder(values.folder);
          notify('success', t('createSuccess', { ns: 'admin/common' }));
        }
        setSubmitForm(true);
      } catch (e: unknown) {
        catchError(e, (message) => {
          notify('error', message);
        });
        handleApiError(e, setFieldError);
      }
      setDisableSubmit(false);
    },
  });

  const selectedTags = form.values.tags || [];

  useEffect(() => {
    props.editData && setSelectedFiles(props.editData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.editData]);

  useEffect(() => {
    props.selectedFiles && props.selectedFiles(selectedFiles);
    handleSetOpen(false);
    setSubmitForm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitForm]);

  useEffect(() => {
    props.jobId &&
      (async () => {
        try {
          const result = await dispatch(handleGetJobFolders({ id: props.jobId, data: props.data ?? {} })).unwrap();
          setFolders(result.responses);
          const imageFolder = result.responses?.find((folder) => folder.code === 'image');
          await form.setFieldValue('folder', imageFolder?.id);
        } catch (e) {
          handleApiError(e);
        }
      })();

    props.customerId &&
      (async () => {
        try {
          const result = await dispatch(handleGetJobFolders({ id: props.customerId, data: props.data ?? {} })).unwrap();
          setFolders(result.responses);
          const imageFolder = result.responses?.find((folder) => folder.code === 'image');
          await form.setFieldValue('folder', imageFolder?.id);
        } catch (e) {
          handleApiError(e);
        }
      })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    if (props.open && props.optionUploadFile.selectFromProject && props.jobId) {
      (async () => {
        try {
          const jobFile = await dispatch(handleGetJobFiles({ id: props.jobId, data: { limit: 0 } })).unwrap();
          const newJobFile = jobFile.responses.map((item) => ({
            ...item,
            checked: false,
          }));

          setJobFile(newJobFile);
        } catch (e) {
          handleApiError(e);
        }
      })();
    } else if (props.open && props.optionUploadFile.selectFromProject && props.customerId) {
      (async () => {
        try {
          const customerFiles = await dispatch(handleGetFiles({ id: props.customerId, data: { limit: 0 } })).unwrap();
          const newCustomerFile = customerFiles.responses.map((item) => ({
            ...item,
            checked: false,
          }));

          setJobFile(newCustomerFile);
        } catch (e) {
          handleApiError(e);
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const handleSetOpen = (open: boolean) => {
    form.resetForm();
    setUploadFiles([]);
    if (props.setOpen) {
      props.setOpen(open);
    }
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const handleDeleteImage = (index: number) => {
    const newFiles = uploadFiles.filter((obj, key) => {
      return key !== index;
    });
    setUploadFiles(newFiles);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const key = Number(name.split('.')[1]);
    if (name.includes('name') && uploadFiles[key]) uploadFiles[key].name = value;
    if (name.includes('note') && uploadFiles[key]) uploadFiles[key].note = value;
    setUploadFiles([...uploadFiles]);
  };

  const handleUploadFile = (newFiles: UploadFile[]) => {
    let newFilesObject: Array<UploadFileWithNote> = [];
    // eslint-disable-next-line array-callback-return
    newFiles.map((file, index) => {
      newFilesObject[index] = {
        name: file.file?.name ? file.file.name.split('.')[0] : '',
        url: file.url ?? (file.file ? URL.createObjectURL(file.file) : ''),
        type: file.file?.type ? (file.file.type.includes('image') ? FileTypeEnum.IMAGE : FileTypeEnum.ANY) : '',
        fileExtension: file.file?.name ? file.file.name.split('.')[1] : '',
        file: file.file,
      };
    });
    setUploadFiles([...uploadFiles, ...newFilesObject]);
  };
  const handleCancel = () => {
    handleSetOpen(false);
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    if (checked) {
      const newSelectedFile = jobFile.filter((file) => file.fileId === value);
      setSelectedFiles([...selectedFiles, ...newSelectedFile]);
    } else {
      const newSelectedFiles = selectedFiles.filter((file) => file.fileId !== value);
      setSelectedFiles(newSelectedFiles);
    }
  };

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) setSelectedFiles(jobFile);
    else setSelectedFiles([]);
  };
  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {t('estimate.fileItem.uploadFile')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent className='p-0'>
        <Box>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={value} onChange={handleChangeTab} aria-label='basic tabs example'>
              {props.optionUploadFile.uploadFile && (
                <Tab
                  label={t('estimate.fileItem.uploadNew')}
                  id='simple-tab-uploadFile'
                  aria-controls='simple-tabpanel-uploadFile'
                  value='uploadFile'
                />
              )}{' '}
              {props.optionUploadFile.selectFromProject && (
                <Tab
                  label={t('estimate.fileItem.selectFromProject')}
                  id='simple-tab-createFromProject'
                  aria-controls='simple-tabpanel-createFromProject'
                  value='createFromProject'
                />
              )}
            </Tabs>
          </Box>
          {/* start upload file */}
          <CustomTabPanel value={value} index='uploadFile'>
            <Box className='pb-[15px] flex w-1/4 items-center'>
              <Typography className='pr-2'>{t('estimate.fileItem.folder')}</Typography>
              <BaseSelect
                name='folder'
                value={form.values?.folder}
                options={folders}
                onChange={form.handleChange}
                error={Boolean(form.errors?.folder)}
                helperText={form.errors?.folder}
              ></BaseSelect>
            </Box>
            <Box className='pb-[15px]'>
              <InputLabel>
                {t('estimate.fileItem.tags')}
                <span className=''> {t('estimate.fileItem.tagNote')}</span>
              </InputLabel>
              <Autocomplete
                multiple
                disableClearable
                value={form.values.tags || []}
                onChange={(event, newValue) => form.setFieldValue('tags', newValue)}
                options={tags}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                popupIcon={null}
                renderTags={() => null}
                disableCloseOnSelect
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='outlined'
                    placeholder={t('searchPlaceholder')}
                    InputProps={{
                      ...params.InputProps,
                      style: {
                        paddingTop: 0,
                        paddingBottom: 0,
                      },
                      endAdornment: (
                        <>
                          <SearchIcon className='w-[16px] mr-[1px]' />
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <div className='mt-[5px]'>
                {selectedTags.map((tag) => (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    onDelete={() => {
                      const newTags = selectedTags.filter((t) => t.id !== tag.id);
                      form.setFieldValue('tags', newTags);
                    }}
                    className='mr-[10px]'
                  />
                ))}
              </div>
            </Box>
            <Box>
              <Box component='form'>
                <BaseUploadFile
                  isSubmitonDrop={true}
                  isMultiple={true}
                  onChange={(files: Array<UploadFile>) => handleUploadFile(files)}
                />
              </Box>
              <Box className='w-full'>
                {uploadFiles?.map((file, index) => (
                  <Box key={index} className='border-2 border-solid my-2 relative'>
                    <IconButton className='absolute right-0 top-0' onClick={() => handleDeleteImage(index)}>
                      <DeleteIcon className='w-[12px] h-[12px]' />
                    </IconButton>
                    <Box className='flex justify-flex-start w-full'>
                      <input type='file' hidden name={`files.${index}.file`} />
                      <Box onClick={() => window.open(file?.url)} className='w-[100px] h-[100px]'>
                        {file?.type === FileTypeEnum.IMAGE ? (
                          <BaseImage src={file?.url} className='w-full h-full object-cover' />
                        ) : (
                          <FileIcon className='mx-auto my-[15px] w-[70px] h-[70px]' />
                        )}
                      </Box>
                      <Box className='block border-l-2 px-[50px] flex-1'>
                        <Box className='flex mt-3 w-full'>
                          <InputLabel className='w-[100px] content-center	'> {t('estimate.fileItem.name')}</InputLabel>
                          <TextField
                            name={`files.${index}.name`}
                            data-key={index}
                            value={file.name}
                            onChange={handleChange}
                            InputProps={{
                              endAdornment: <InputAdornment position='end'>.{file.fileExtension}</InputAdornment>,
                            }}
                          />
                        </Box>
                        <Box className='flex mt-3 w-full'>
                          <InputLabel className='w-[100px] content-center'>{t('estimate.fileItem.note')}</InputLabel>
                          <TextField
                            name={`files.${index}.note`}
                            data-key={index}
                            value={file.note}
                            type='text'
                            onChange={handleChange}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </CustomTabPanel>
          {/* end upload file */}

          {/* start select from project */}
          <CustomTabPanel value={value} index='createFromProject'>
            <Box className='flex justify-end'>
              <FormControlLabel
                control={
                  <Checkbox
                    className='w-[20px] h-[20px]'
                    color='primary'
                    onChange={(event) => handleSelectAll(event)}
                  />
                }
                label={t('selectAll', { ns: 'admin/common' })}
                value={true}
              />
            </Box>
            <Box className='w-full'>
              {jobFile?.map((file, index) => (
                <Box key={index} className='border-2 border-solid my-2 relative'>
                  <IconButton className='absolute right-0 top-0'>
                    <Checkbox
                      className='w-[20px] h-[20px]'
                      color='primary'
                      value={file.fileId}
                      onChange={handleCheckboxChange}
                      checked={selectedFiles.some((selectedFile) => file.fileId === selectedFile.fileId)}
                    />
                  </IconButton>
                  <Box className='flex justify-flex-start w-full'>
                    <Box onClick={() => window.open(file?.url)} className='w-[100px] h-[100px]'>
                      {file?.type === FileTypeEnum.IMAGE ? (
                        <BaseImage src={file?.url} className='w-full h-full object-cover' />
                      ) : (
                        <FileIcon className='mx-auto my-[15px] w-[70px] h-[70px]' />
                      )}
                    </Box>
                    <Box className='block border-l-2 px-[50px] flex-1'>
                      <Box className='flex mt-3 w-full'>
                        <InputLabel className='w-[100px] content-center	'> {t('estimate.fileItem.name')}</InputLabel>
                        <Typography>{file.name}</Typography>
                      </Box>
                      <Box className='flex mt-3 w-full'>
                        <InputLabel className='w-[100px] content-center'> {t('estimate.fileItem.note')}</InputLabel>
                        <Typography>{file.note}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </CustomTabPanel>
          {/* end select from project */}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          disabled={disableSubmit}
          onClick={() => {
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default memo(UploadFileDialog);
